/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Box, Card, Typography, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { H3 } from "components/generals/_Titles";
import MDBox from "dashboard-template/components/MDBox";
import DefaultDashboardLayout from "layouts/dashboard/DefaultDashboardLayout";
import { useTranslation } from "react-i18next";
import NotificationWidget from "components/widgets/NotificationWidget";
import { FC, useEffect, useState } from "react";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { getRangeDateQuery } from "utils/http.utils";
import moment from "moment";
import { AppointmentInterface, HeaderEnum, QueryResultInterface, VisitStatusEnum } from "medicalsystem-shared";
import { useNavigate } from "react-router-dom";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";  
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AppointmentCard from "components/generals/AppointmentCard";
import AppointmentForm from "components/forms/AppointmentForm";
import { FormActionType } from "types";
import { Modal } from "components/generals/Modal";
import { useSelector } from "react-redux";
import { selectIsPatient } from "store/slices/auth.slice";
import { getNewAppointment } from "utils/model.utils";
import { useNavigationHistory } from "hooks/navigation.hook";
import { useMaterialUIController } from "context/ThemeContext";
import { getAppointmentTimeRange } from "utils/date.utils";
import HtmlTooltip from "components/generals/HtmlTooltip";

/**
 * Main dashboard home page displaying today's appointments and notifications.
 * 
 * Features:
 * - Today's appointments grid view with color-coded status cards
 * - Real-time appointment status visualization:
 *   - Completed (green): Check-out status
 *   - On-time (blue): Active appointments not past due
 *   - Past due (yellow): Check-in appointments after scheduled time
 *   - Cancelled (red): Cancelled appointments
 * - Interactive appointment cards with click to view/edit details
 * - Sortable appointment list (closest first / farthest first)
 * - Quick appointment creation modal (for non-patients)
 * - Calendar navigation button
 * - Notification widget sidebar with real-time updates
 * - Legend explaining color-coded appointment statuses
 * - Appointment count summary
 * - Navigation history integration for direct appointment opening
 * - Dark mode support with adjusted colors
 * 
 * Appointment Management:
 * - Fetches appointments for current day on mount
 * - Displays appointment details in modal on card click
 * - Supports creating new appointments via modal form
 * - Refreshes appointment list after create/update operations
 * - Restores appointment from navigation history if available
 * 
 * Patient View:
 * - Hides "New Appointment" button for patient role users
 * - Shows all appointments where patient is involved
 * 
 * @returns Dashboard home page component
 */
const DashboardHome: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, _error, sendRequest] = useHttp();
  const [todayAppointments, setTodayAppointments] = useState<AppointmentInterface[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentInterface | null>(null);
  const isPatient = useSelector(selectIsPatient);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [newAppointmentDetails, setNewAppointmentDetails] = useState<AppointmentInterface | null>(null);
  const [sortClosestFirst, setSortClosestFirst] = useState(false);
  const { getSavedEntityIdFromHistory, clearSavedEntityFromHistory } = useNavigationHistory();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  useEffect(() => {
    const startOfDay = moment().startOf("day").toDate();
    const endOfDay = moment().endOf("day").toDate();
    
    sendRequest(
      CoreAPI_URLs.APPOINTMENTS_GET_ALL + getRangeDateQuery(startOfDay, endOfDay),
      onGetTodayAppointmentsHandler,
      "GET"
    );
  }, []);

  useEffect(() => {
    const savedEntityId = getSavedEntityIdFromHistory();
    if (savedEntityId) {
      sendRequest(
        CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
        (appointmentData: AppointmentInterface) => {
          if (appointmentData) {
            setSelectedAppointment(appointmentData);
            clearSavedEntityFromHistory(); // Limpiar después de usar
          }
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.RESOURCE_ID, value: savedEntityId }]
      );
    }
  }, []); 

  /**
   * Callback handler for today's appointments data retrieval.
   * Sorts appointments by time (closest or farthest first based on state).
   * 
   * @param {QueryResultInterface<AppointmentInterface>} data - Query result with appointments
   * @returns {void}
   */
  const onGetTodayAppointmentsHandler = (data: QueryResultInterface<AppointmentInterface>) => {
    const sortedAppointments = data.results.sort((a, b) => {
      const timeA = moment(a.arrivalDateTime);
      const timeB = moment(b.arrivalDateTime);
      return sortClosestFirst ? timeB.diff(timeA) : timeA.diff(timeB);
    });
    setTodayAppointments(sortedAppointments);
  };

  /**
   * Toggles appointment sort order between closest first and farthest first.
   * Re-sorts the appointment list based on arrival time.
   * 
   * @returns {void}
   */
  const handleSortToggle = () => {
    setSortClosestFirst(!sortClosestFirst);
    const sortedAppointments = [...todayAppointments].sort((a, b) => {
      const timeA = moment(a.arrivalDateTime);
      const timeB = moment(b.arrivalDateTime);
      return !sortClosestFirst ? timeB.diff(timeA) : timeA.diff(timeB);
    });
    setTodayAppointments(sortedAppointments);
  };

  /**
   * Handles appointment card click event.
   * Opens appointment detail modal with selected appointment data.
   * 
   * @param {AppointmentInterface} appointment - Selected appointment
   * @returns {void}
   */
  const handleAppointmentClick = (appointment: AppointmentInterface) => {
    setSelectedAppointment(appointment);
  };

  /**
   * Closes appointment detail modal.
   * Clears selected appointment state.
   * 
   * @returns {void}
   */
  const handleCloseAppointmentDetail = () => {
    setSelectedAppointment(null);
  };

  /**
   * Opens new appointment creation modal.
   * Initializes new appointment with current date/time.
   * 
   * @returns {void}
   */
  const handleNewAppointment = () => {
    const currentDate = new Date();
    setNewAppointmentDetails(getNewAppointment(currentDate));
    setShowNewAppointmentModal(true);
  };

  /**
   * Closes new appointment modal.
   * Clears new appointment details state.
   * 
   * @returns {void}
   */
  const handleCloseNewAppointmentModal = () => {
    setShowNewAppointmentModal(false);
    setNewAppointmentDetails(null);
  };

  /**
   * Navigates to full appointments calendar page.
   * 
   * @returns {void}
   */
  const handleShowCalendar = () => {
    navigate("/appointments");
  };

  /**
   * Refreshes today's appointments list from API.
   * Fetches appointments for current day and updates display.
   * 
   * @returns {void}
   */
  const refreshAppointments = () => {
    const startOfDay = moment().startOf("day").toDate();
    const endOfDay = moment().endOf("day").toDate();
    
    sendRequest(
      CoreAPI_URLs.APPOINTMENTS_GET_ALL + getRangeDateQuery(startOfDay, endOfDay),
      onGetTodayAppointmentsHandler,
      "GET"
    );
  };

  const appointmentsCount = todayAppointments.length;

  /**
   * Determines appointment card background color based on status and timing.
   * 
   * Color Logic:
   * - CHECK_OUT: Green (completed)
   * - CANCEL: Red (cancelled)
   * - CHECK_IN (past due): Yellow (late)
   * - CHECK_IN (on time): Blue (scheduled)
   * 
   * Returns light colors for light mode, dark colors for dark mode.
   * 
   * @param {AppointmentInterface} appointment - Appointment to colorize
   * @returns {string} Hex color code for card background
   */
  const getAppointmentCardColor = (appointment: AppointmentInterface): string => {
    const now = moment();
    const appointmentTime = moment(appointment.arrivalDateTime);
    const isPastDue = appointmentTime.isBefore(now);
  
    let lightColor = "";
    let darkColor = "";
    
    if (appointment.visitStatus === VisitStatusEnum.CANCEL) {
      lightColor = "#ffebee";
      darkColor = "#751414";
    } else if (appointment.visitStatus === VisitStatusEnum.CHECK_IN && isPastDue) {
      lightColor = "#fff8e1";
      darkColor = "#BA9C06";
    } else if (appointment.visitStatus === VisitStatusEnum.CHECK_OUT) {
      lightColor = "#e8f5e8";
      darkColor = "#2F6B05";
    } else {
      lightColor = "#e3f2fd";
      darkColor = "#05496B";
    }
    const finalColor = darkMode ? darkColor : lightColor;
    return finalColor;
  };

  /**
   * Gets tooltip text for sort button based on current sort direction.
   * 
   * @returns {string} Localized tooltip text
   */
  const getSortTooltipText = (): string => {
    return sortClosestFirst ?  t("dashboard.orderByClosestFirst") : t("dashboard.orderByFarthestFirst");
  };
  const getSortCardsText = (): string => {
    return t("dashboard.orderedBy")+" " + (sortClosestFirst ?  t("dashboard.farthestFirst") : t("dashboard.closestFirst"));
  };

  return (
    <DefaultDashboardLayout>
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
          <Card className="dashboard-appointments__container">
                <Box>
                  <Box className="dashboard-appointments__header">
                    <Box className="dashboard-appointments__title-wrapper">
                      <H3>{t("dashboard.todayAppointments")}</H3>
                      <Typography variant="body2" color={darkMode ? "white" : "text.secondary"}>
                        {appointmentsCount === 0 
                          ? t("dashboard.noAppointmentsToday")
                          : `${appointmentsCount} ${appointmentsCount === 1 ? t("dashboard.appointment") : t("dashboard.appointments")} ${t("dashboard.programmed")}`
                        }
                      </Typography>
                      <Box className="dashboard-appointments__legend">
                        <Box className="dashboard-appointments__legend-inner">
                            <Box className="dashboard-appointments__legend-items">
                            <Box className="dashboard-appointments__legend-item">
                                <Box className={`dashboard-appointments__legend-box dashboard-appointments__legend-box--completed ${darkMode ? 'dark-mode' : ''}`} />
                                <Typography variant="caption" color={darkMode ? "white" : "text.secondary"} className="text--caption-small">
                                  {t("dashboard.completedAppointments")}
                                </Typography>
                              </Box>
                              <Box className="dashboard-appointments__legend-item">
                                <Box className={`dashboard-appointments__legend-box dashboard-appointments__legend-box--scheduled ${darkMode ? 'dark-mode' : ''}`} />
                                <Typography variant="caption" color={darkMode ? "white" : "text.secondary"} className="text--caption-small">
                                  {t("dashboard.onTimeAppointments")}
                                </Typography>
                              </Box>
                              <Box className="dashboard-appointments__legend-item">
                                <Box className={`dashboard-appointments__legend-box dashboard-appointments__legend-box--past-due ${darkMode ? 'dark-mode' : ''}`} />
                                <Typography variant="caption" color={darkMode ? "white" : "text.secondary"} className="text--caption-small">
                                  {t("dashboard.pastDueAppointments")}
                                </Typography>
                              </Box>
                              <Box className="dashboard-appointments__legend-item">
                                <Box className={`dashboard-appointments__legend-box dashboard-appointments__legend-box--cancelled ${darkMode ? 'dark-mode' : ''}`} />
                                <Typography variant="caption" color={darkMode ? "white" : "text.secondary"} className="text--caption-small">
                                  {t("dashboard.cancelledAppointments")}
                                </Typography>
                              </Box>
                            </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box className="dashboard-appointments__actions">
                    {todayAppointments.length > 1 && (
                        <Box className="dashboard-appointments__sort-info">
                          <Typography variant="caption" color={darkMode ? "white" : "text.secondary"} className="text--caption-small">
                            {getSortCardsText()}
                          </Typography>
                          <Tooltip title={getSortTooltipText()}>
                            <IconButton 
                              onClick={handleSortToggle}
                              color="default"
                            >
                              {sortClosestFirst ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {!isPatient && (
                        <Tooltip title={t("dashboard.newAppointment")}>
                          <IconButton 
                            onClick={handleNewAppointment}
                            color="default"
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t("dashboard.goToCalendar")}>
                        <IconButton 
                          onClick={handleShowCalendar}
                          color="default"
                        >
                          <CalendarTodayIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  {isLoading ? (
                   <Box display="flex" justifyContent="center" py={4}>
                     <Typography>
                       {t("common.loading")}
                     </Typography>
                   </Box>
                  ) : (
                   <Box>
                     {todayAppointments.length === 0 ? (
                       <Box className="dashboard-appointments__empty">
                         <CalendarTodayIcon className={`icon--empty-state ${darkMode ? 'dark-mode' : ''}`} />
                         <Typography variant="h6" color={darkMode ? "white" : "text.secondary"} mb={1}>
                           {t("dashboard.noAppointmentsToday")}
                         </Typography>
                         <Typography variant="body2" color={darkMode ? "white" : "text.secondary"}>
                           {t("dashboard.noAppointmentsMessage")}
                         </Typography>
                       </Box>
                     ) : (
                      <Grid container spacing={2}>
                        {todayAppointments.map((appointment) => (
                          <Grid item xs={12} sm={6} md={4} key={appointment.id}>
                            <HtmlTooltip
                              title={
                                <Box px={1} className="fc-event-content">
                                  <Stack>{appointment.reason}</Stack>
                                  <Stack>{appointment.provider?.person.name + " " + appointment.provider?.person.lastname}</Stack>
                                  <Stack>{appointment.patient?.person.name + " " + appointment.patient?.person.lastname}</Stack>
                                  <Stack>
                                    {getAppointmentTimeRange(appointment.arrivalDateTime,  moment(appointment.arrivalDateTime).add(appointment.duration, "minutes").toDate())}
                                  </Stack>
                                </Box>
                              }
                              placement="top"
                            >
                              <div>
                                <AppointmentCard 
                                  appointment={appointment}
                                  onClick={() => handleAppointmentClick(appointment)}
                                  color={getAppointmentCardColor(appointment)}
                                />
                              </div>
                            </HtmlTooltip>
                          </Grid>
                        ))}
                      </Grid>
                     )}
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Box className="dashboard-appointments__notification">
              <NotificationWidget
                shouldFetchData={false}
              />
            </Box>
          </Grid>
        </Grid>
      </MDBox>
      {selectedAppointment && (
        <Modal
          open={!!selectedAppointment}
          onClose={handleCloseAppointmentDetail}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          >
          <AppointmentForm
          model={selectedAppointment}
          action={FormActionType.MODIFY}
          isModal={true}
          onCloseAction={handleCloseAppointmentDetail}
          updateListHandler={refreshAppointments}
        />
        </Modal>
      )}
      {showNewAppointmentModal && (
        <Modal
          open={showNewAppointmentModal}
          onClose={handleCloseNewAppointmentModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          >
          <AppointmentForm
          model={newAppointmentDetails}
          action={FormActionType.NEW}
          isModal={true}
          onCloseAction={handleCloseNewAppointmentModal}
          updateListHandler={refreshAppointments}
          comesFromBilling={false}
        />
        </Modal>
      )}
    </DefaultDashboardLayout>
  );
};

export default DashboardHome;
